import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/src/utils';
import { Matcher, SelectSingleEventHandler } from 'react-day-picker';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Button } from '../../ui/button';
import dayjs from 'dayjs';
import Typo from '../Typo';
import LucideIcon from '../Icons/LucideIcon';
import LocalIcon from '../Icons/LocalIcon';
import ClearSvg from '@/src/assets/icons/icon-input-clear.svg';
import CalendarWithTimePicker from '../../molecule/CalendarWithTimePicker';
import { formatter } from '@/src/utils/formatter';

const datePickerSizeData = {
  medium: 'px-[12px] py-[8px] h-[40px]',
  small: 'px-[12px] py-[6px] h-[32px]',
};

export interface DatePickerProps {
  value?: Date;
  onChange?: SelectSingleEventHandler;
  dateFormat?: string;
  valueFormat?: string;
  triggerProps?: React.ComponentPropsWithoutRef<
    typeof PopoverPrimitive.Trigger
  >;
  rootProps?: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>;
  placeholder?: string;
  size?: keyof typeof datePickerSizeData;
  allowClear?: boolean;
  isTimePicker?: boolean;
  hourStart?: string;
  hourEnd?: string;
  disabledDate?: Matcher | Matcher[] | undefined;
}

function DatePicker({
  value,
  onChange,
  dateFormat = 'YYYY-MM-DD',
  valueFormat,
  triggerProps,
  rootProps,
  placeholder,
  size = 'medium',
  allowClear = true,
  isTimePicker,
  hourStart = '00',
  hourEnd = '23',
  disabledDate,
}: DatePickerProps) {
  const [isHover, setIsHover] = useState(false);

  const disabledTriggerStyle = triggerProps?.disabled
    ? 'bg-gray1 border-gray3 text-gray6'
    : '';

  const renderRightIcon = () => {
    if (allowClear && isHover && value) {
      return (
        <LocalIcon
          iconSrc={ClearSvg}
          iconSize={16}
          onClick={e => {
            onChange?.(undefined, undefined as any, {}, e);
          }}
          className="flex flex-shrink-0"
          color={triggerProps?.disabled ? 'gray6' : 'gray9'}
        />
      );
    }

    return (
      <LucideIcon
        size={16}
        name="Calendar"
        color={triggerProps?.disabled ? 'gray6' : 'gray9'}
        className="flex flex-shrink-0"
      />
    );
  };

  return (
    <Popover {...rootProps}>
      <PopoverTrigger
        asChild
        className={cn(
          'border bg-white focus-visible:border-gray9 focus-visible:ring-0 focus-visible:ring-offset-0',
          disabledTriggerStyle,
          triggerProps?.className,
        )}
        onMouseOver={() => {
          setIsHover(true);
        }}
        onMouseLeave={() => {
          setIsHover(false);
        }}
        {...triggerProps}
      >
        <Button
          variant="outline"
          className={cn(
            `w-[100%] items-center justify-between ${datePickerSizeData[size]} overflow-hidden disabled:opacity-100 `,
            !value && 'text-muted-foreground',
          )}
        >
          {value ? (
            <Typo typoType="b14r">
              {dayjs(value).format(valueFormat || dateFormat)}
            </Typo>
          ) : (
            <Typo typoType="b14r" typoColor="gray4">
              {placeholder ?? 'YYYY-MM-DD'}
            </Typo>
          )}

          {renderRightIcon()}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <section className="flex h-full">
          <Calendar
            mode="single"
            disabled={disabledDate}
            selected={value}
            onSelect={date => {
              if (isTimePicker && value && date && onChange) {
                const updatedTime = dayjs()
                  .year(date.getFullYear())
                  .month(date.getMonth())
                  .date(date.getDate())
                  .hour(value.getHours())
                  .minute(value.getMinutes())
                  .second(value.getSeconds())
                  .toDate();

                return onChange(
                  updatedTime,
                  updatedTime,
                  {},
                  {} as React.MouseEvent,
                );
              }
              const initDate = date
                ? dayjs(date)
                    .hour(formatter.STRING_TO_NUM(hourStart))
                    .minute(0)
                    .second(0)
                    .toDate()
                : undefined;

              onChange &&
                onChange(
                  initDate,
                  initDate as Date,
                  {},
                  {} as React.MouseEvent,
                );
            }}
            initialFocus
          />
          {isTimePicker && (
            <CalendarWithTimePicker
              value={value}
              onChange={onChange}
              dateFormat={dateFormat}
              hourStart={hourStart}
              hourEnd={hourEnd}
            />
          )}
        </section>
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
